import { SessionStorage } from 'core-lib/constants/storage';
import { WEBSITE_URL } from 'core-lib/data/common';
import formatCurrency from 'core-lib/utils/currency';
import { trackCustomGTMEvent } from 'core-lib/utils/events';
import { EventCategoryName } from 'core-lib/types/Events';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'ui-v2/components/Button';
import TransitionTimeout from 'ui-v2/components/Transitions/TransitionTimeout';
import { Typography } from 'ui-v2/components/Typography';
import Icon from 'ui-v2/components/Icon/Icon';
import parkplusStoreV2RedirectionRoutes from '../../constants/parkplusStoreV2RedirectionRoutes';
import useParkplusStoreV2 from '../../hooks/useParkplusStoreV2';
import { calculateTotalCartValue } from '../../utils';
import { trackCtaClickedStoreV2 } from '../../utils/events';
import DeliveryFeeStrip from '../DeliveryFeeStrip';
import CartActionPopup from '../Popups/CartActionPopup';
import { ChangedPricePopup } from '../Popups/ChangedPricePopup/ChangedPricePopup';
import StackImages from '../StackImages';
import s from './CartCta.module.scss';

export const CartCta = () => {
  // state
  const [showCartAction, setShowCartAction] = useState(false);

  const { profile } = useSelector((state: any) => state.auth);

  // declaration
  const router = useRouter();
  const { basePath, asPath } = router;
  const pageUrl = `${WEBSITE_URL}${basePath}${asPath}`;

  // custom hooks
  const { cartData } = useParkplusStoreV2();

  // constants
  const cartLength = cartData?.data?.length;

  useEffect(() => {
    if (!cartLength) setShowCartAction(false);
  }, [cartLength]);

  if (!cartLength) {
    return;
  }

  return (
    <TransitionTimeout disableOnPopState animation={{ type: 'btt', timeout: 's' }}>
      <div className={s.root}>
        {
          <DeliveryFeeStrip
            text={cartData.stripData?.text}
            icon={cartData.stripData?.icon}
            cartData={cartData}
          />
        }
        <div className={s.cta}>
          <div
            className={s.left}
            onClick={() => {
              setShowCartAction(() => !showCartAction);
            }}
          >
            <StackImages cartData={cartData?.data} />
            <div className={s.cartInfo}>
              <div className={s.price}>
                <Typography
                  label={formatCurrency(calculateTotalCartValue(cartData))}
                  variant="label_1_bold"
                />
                <div>
                  <Typography label={`Cart subtotal`} variant="label_4_medium" color="secondary" />
                  {showCartAction ? (
                    <Icon name="ChevronDownBold" width={14} height={14} color="secondary" />
                  ) : (
                    <Icon name="ChevronUpBold" width={14} height={14} color="secondary" />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={s.right}>
            <Button
              size="medium"
              variant="primary"
              label={`View cart ${cartLength > 0 ? `(${cartLength})` : ''}`}
              onClick={() => {
                router.push(`${parkplusStoreV2RedirectionRoutes.cart}`);
                trackCtaClickedStoreV2('', 'view_cart');
                trackCustomGTMEvent({
                  eventName: 'pm_35_view_cart',
                  attributes: {
                    category: EventCategoryName.Store,
                    page_url: pageUrl,
                  },
                });
              }}
              fullWidth
            />
          </div>
        </div>
        <CartActionPopup
          show={showCartAction}
          onClose={() => {
            setShowCartAction(false);
          }}
        />
      </div>
      <ChangedPricePopup />
      <div className={s.paddingBottom} />
    </TransitionTimeout>
  );
};
